import { create } from 'zustand';
import { ZONES } from './config/zones';
import { shared } from './three/shared';

const WEATHERS = ['sunny', 'mist', 'storm'];
const rnd = () => Math.floor(Math.random() * 1000000);
const initWeather = WEATHERS[Math.floor(Math.random() * WEATHERS.length)];
const initTime = Math.random();
const initRain = initWeather === 'storm' ? 0.5 + Math.random() * 0.5 : 0;
const initBg = initWeather === 'storm' ? 'sunny' : initWeather;
shared.env.dayTime = initTime; // session-randomized starting time of day

export const useStore = create((set, get) => ({
  // Session
  entered: false,
  launched: false,
  loading: false,

  // Environment (HUD-facing, throttled from the 3D WorldClock)
  timeLabel: 'DAY',
  isNight: false,
  stormActive: initRain > 0,
  audioEnabled: true,
  guideVolume: 1, // Harpy Eagle briefing volume (0..1); ambience is independent
  panelsHidden: false, // Hide Panels toggle: hides all floating 3D zone labels/placards
  activeSideCard: null, // zone index whose info side-card is open (null = closed)
  joysticks: false, // desktop opt-in for the on-screen dual joysticks (auto on touch)
  haptics: true, // D-pad vibration feedback on mobile
  zoneMenuOpen: false,

  // Navigation
  mode: 'suv', // 'suv' | 'helicopter' | 'boat'

  // Environment deck
  weather: initBg, // background preset: 'sunny' | 'mist'
  rainIntensity: initRain, // 0..1 Outrage-Storm rain density
  autoCycle: true,
  timeValue: initTime,
  worldSeed: rnd(),
  hudOpen: false,
  briefings: {},

  // Tour
  tourActive: false,
  currentZone: 0, // index 0..9
  bankFocus: null, // 'progressive' | 'traditional' | null
  replayNonce: 0,
  neutralNonce: 0, // Neutral Channel briefing trigger (Boat mode)
  tourCompleteNonce: 0, // bumps when a guided tour finishes → auto-share postcard
  bankNonce: 0, // click a bank → play just that bank's briefing
  bankRequest: null, // 'progressive' | 'traditional'
  compareNonce: 0, // A/B compare: play Liberal then Conservative back-to-back
  bridgeCueNonce: 0, // eagle prompts to use the bridge the first time SUV hits water

  // Narration
  caption: '',
  captionLabel: '',
  narrating: false,
  fieldCard: null, // { name, text } — Field Guide fly-by name card

  // Live player transform (for minimap)
  playerPos: [0, 0],
  playerYaw: 0,

  // Actions
  enter: () => set({ entered: true }),
  launchSimulation: () => set({ entered: true, launched: true, mode: 'suv', tourActive: false, bankFocus: null }),
  setEnvHud: (timeLabel, isNight) => set((s) => (s.timeLabel === timeLabel && s.isNight === isNight ? s : { timeLabel, isNight })),
  setStormActive: (stormActive) => set((s) => (s.stormActive === stormActive ? s : { stormActive })),
  toggleAudio: () => set((s) => ({ audioEnabled: !s.audioEnabled })),
  setGuideVolume: (guideVolume) => set({ guideVolume }),
  togglePanelsHidden: () => set((s) => ({ panelsHidden: !s.panelsHidden })),
  setSideCard: (i) => set({ activeSideCard: i }),
  closeSideCard: () => set({ activeSideCard: null }),
  toggleJoysticks: () => set((s) => ({ joysticks: !s.joysticks })),
  toggleHaptics: () => set((s) => ({ haptics: !s.haptics })),
  narrateBank: (bank) => set((s) => ({ bankNonce: s.bankNonce + 1, bankRequest: bank })),
  narrateCompare: () => set((s) => ({ compareNonce: s.compareNonce + 1 })),
  triggerBridgeCue: () => set((s) => ({ bridgeCueNonce: s.bridgeCueNonce + 1 })),
  setZoneMenuOpen: (zoneMenuOpen) => set({ zoneMenuOpen }),
  setMode: (mode) => set({ mode }),
  setWeather: (weather) => set({ weather }),
  setRainIntensity: (rainIntensity) => set({ rainIntensity, stormActive: rainIntensity > 0.02 }),
  setFieldCard: (fieldCard) => set({ fieldCard }),
  toggleAutoCycle: () => set((s) => ({ autoCycle: !s.autoCycle })),
  setTimeValue: (timeValue) => set((s) => (Math.abs(s.timeValue - timeValue) < 0.002 ? s : { timeValue })),
  toggleHud: () => set((s) => ({ hudOpen: !s.hudOpen })),
  setBriefingCache: (id, data) => set((s) => (s.briefings[id] ? s : { briefings: { ...s.briefings, [id]: data } })),

  // Organic Randomizer — reshuffle weather, time, and all procedural spawns.
  reshuffle: () => set(() => {
    const tv = Math.random();
    shared.env.dayTime = tv;
    const rain = Math.random() > 0.6 ? 0.4 + Math.random() * 0.6 : 0;
    const bg = Math.random() > 0.5 ? 'mist' : 'sunny';
    return { worldSeed: rnd(), weather: bg, rainIntensity: rain, stormActive: rain > 0, timeValue: tv, autoCycle: true };
  }),
  // Reset — restore the living auto-cycle after manual overrides.
  resetEnv: () => set(() => {
    const tv = Math.random();
    shared.env.dayTime = tv;
    return { autoCycle: true, weather: 'sunny', rainIntensity: 0, stormActive: false, timeValue: tv, worldSeed: rnd() };
  }),

  startTour: () =>
    set({ tourActive: true, currentZone: 0, replayNonce: get().replayNonce + 1 }),
  stopTour: () =>
    set({ tourActive: false, bankFocus: null, caption: '', captionLabel: '', narrating: false }),

  nextZone: () => {
    const i = get().currentZone;
    if (i >= ZONES.length - 1) {
      set({
        tourActive: false,
        bankFocus: null,
        narrating: false,
        caption: 'You have traversed all ten biomes of bias. The tour is complete.',
        captionLabel: 'THE HARPY EAGLE',
        tourCompleteNonce: get().tourCompleteNonce + 1,
      });
      return;
    }
    set({ currentZone: i + 1, replayNonce: get().replayNonce + 1 });
  },
  prevZone: () => {
    const i = get().currentZone;
    if (i <= 0) return;
    set({ currentZone: i - 1, replayNonce: get().replayNonce + 1 });
  },
  goToZone: (i) =>
    set({ currentZone: i, tourActive: true, replayNonce: get().replayNonce + 1 }),

  replayZone: () => set({ replayNonce: get().replayNonce + 1, tourActive: true }),

  // Single-zone briefing triggered by proximity (no auto-advance)
  narrateZone: (i) => set((s) => ({ currentZone: i, tourActive: false, replayNonce: s.replayNonce + 1 })),

  // Neutral Channel briefing (Boat mode, sailing the central river)
  narrateNeutral: () => set((s) => ({ neutralNonce: s.neutralNonce + 1 })),

  setBankFocus: (bankFocus) => set({ bankFocus }),
  setCaption: (caption, captionLabel = '') => set({ caption, captionLabel }),
  setNarrating: (narrating) => set({ narrating }),
  setLoading: (loading) => set({ loading }),
  setPlayerTransform: (x, z, yaw) => set({ playerPos: [x, z], playerYaw: yaw }),
}));
